import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, FormSpy, useField } from 'react-final-form';
import { connect } from 'react-redux';

import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { PM_SUBJECT } from 'CorePackage/MainModule/routes/routes';
import { getFormStateValues } from 'KnmErpPackage/MainModule/common/formInstanceGetters';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { removeSubjectErvkObjects } from 'KnmPackage/MainModule/common/removeSubjectErvkObjects';
import {
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsNew,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { ErknmSubjectSelectInPm } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectSelectInPm';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';

import { RafpRequestStatusTypeEnum } from '../../../APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { KnmSonkoField } from '../../../KnmPackage/MainModule/components/KnmSonkoField';
import { Section } from '../../../UIPackage/FormModule/components/Section';
import styles from './PmSubject.module.scss';
import { PmSubjectItemCollapse } from './PmSubjectItemCollapse';
import { PmSubjectItem } from './PmSubjectItem';
import { usePmErknmSubjectFields } from '../hooks/usePmErknmSubjectFields';
import { clearPmSubjectFields } from '../common/clearPmSubjectFields';
import { IKnmSubject248ItAccreditationResponse } from '../../../APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { InspectionResultFieldEnum } from '../../../APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { IKnmFormSubject } from '../../../KnmPackage/KnmSubjectModule/interfaces/IKnmFormSubject';
import { KnmMspField } from '../../../KnmPackage/MainModule/components/KnmMspField';
import { ProfKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { IDictionaryProfKindSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryProfKindSelectOption';

interface IPmSubjectProps {
  ervkId: string | undefined;
  className?: string;
  watchOnly: boolean;
  inn: string | undefined;
  ogrn: string | undefined;
  snils: string | undefined;
  organizationAutocomplete: IKnmFormSubject['organizationAutocomplete'];
  okveds: IKnmFormSubject['okveds'] | undefined;
  checkSubjectResult?: IKnmCheckSubjectResult | null;
  isFiz: boolean | undefined;
  rafpNza: string | undefined;
  kpp: string | undefined;
  isNewOrganization: boolean | undefined;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  isSonko: boolean | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  isPresentInItRegistry: boolean | undefined;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  smevItAccreditationRequestStatus: string | undefined;
  ignoreStatus?: boolean;
  name?: string;
  title?: string | null;
  hideAddRemove?: boolean;
  subjectTypeLabel?: string;
  collapse?: boolean;
  getBlockId?: (id: string) => string;
  inspectionResults?: IInspectionResult[];
  organizationsForSelect?: IKnmErknmFormSubject[];
  wasSelectedOrganizationByOtherPassport: boolean;
  isLinkedByImpossibility?: boolean;
  pmType: IDictionaryProfKindSelectOption | undefined;
}

interface IPmSubjectStoreProps {
  formRef: IKnmFormRef | undefined;
  isNewPm: boolean;
  isKnmStatusFillInProcess: boolean;
}

const getDefaultBlockId = (id: string) => id;

const PmSubjectWithoutStore = ({
  ervkId,
  className = '',
  watchOnly,
  organizationAutocomplete,
  okveds,
  checkSubjectResult,
  inn,
  ogrn,
  snils,
  isFiz,
  rafpNza,
  kpp,
  formRef,
  isSonko,
  isNewOrganization,
  subjectType,
  smevRequestStatus,
  inspectionResults,
  isNewPm,
  isPresentInItRegistry,
  itAccreditationResponse,
  smevItAccreditationRequestStatus,
  isKnmStatusFillInProcess,
  ignoreStatus,
  name,
  hideAddRemove,
  title,
  subjectTypeLabel,
  collapse = true,
  getBlockId = getDefaultBlockId,
  organizationsForSelect,
  wasSelectedOrganizationByOtherPassport,
  isLinkedByImpossibility,
  pmType,
}: IPmSubjectProps & IPmSubjectStoreProps) => {
  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();
  const fieldName = name ? `${name}.` : '';

  const isNewFieldName = `${fieldName}isOrganizationNew` as keyof IKnmFormSubject;
  const mspCodeName = `${fieldName}mspCode` as keyof Pick<IKnmFormSubject, 'mspCode'>;
  const isSonkoName = `${fieldName}isSonko` as keyof IKnmFormSubject;

  const nameWithoutDot = fieldName.endsWith('.') ? fieldName.slice(0, -1) : fieldName;
  const subjectFieldNames = usePmErknmSubjectFields({ name: nameWithoutDot, isRemovePrefix: true });

  const pmSubjectBlockId = getBlockId(PM_SUBJECT);

  const [checkByInnMessage, setCheckByInnMessage] = useState<string | undefined>(undefined);

  const isConnectedToErvkObject =
    !!ervkId &&
    !!formRef?.current
      .getFieldState('objectsPm')
      ?.value?.flatMap(object => object.organizationErvkId)
      ?.find(x => x === ervkId);

  const isSubjectWatchOnly: boolean = ignoreStatus
    ? watchOnly || wasSelectedOrganizationByOtherPassport
    : watchOnly || !(isNewPm || isKnmStatusFillInProcess) || !!ervkId || wasSelectedOrganizationByOtherPassport;

  const PmSubjectItemComponent = collapse ? PmSubjectItemCollapse : PmSubjectItem;

  const sectionTitle: string = (title === null ? title : title ?? 'Сведения о контролируемом лице') || '';

  useEffect(() => {
    if (inn && ogrn) {
      dispatchKnmSetLegalEntityData({
        inn,
        ogrn,
      });
    }
  }, [dispatchKnmSetLegalEntityData, inn, ogrn]);

  const addSubject = useCallback(() => {
    formRef?.current.change(isNewFieldName, true);
  }, [formRef]);

  const removeSubject = useCallback(() => {
    // Объекты контроля, добавленные из ЕРВК вместе с этим КЛ, должны удаляться вместе с ним.
    // Актуально в первую очередь для isLinkedByImpossibility: там кнопка удаления КЛ доступна
    // даже при isConnectedToErvkObject, и без каскада ОБК оставался бы в паспорте без своего КЛ.
    removeSubjectErvkObjects({ formRef, ervkId, objectsFieldName: 'objectsPm' });

    // Чистим поля КЛ здесь, а не через ref на PmSubjectItem: пока гармошку ни разу не раскрыли,
    // её содержимое не смонтировано (KnmCollapse), ref пуст, и КЛ не удалялся.
    clearPmSubjectFields({ formRef, subjectFieldNames, withSubjectType: true });
    dispatchKnmSetLegalEntityData(undefined);

    formRef?.current.change(isNewFieldName);
    formRef?.current.change(mspCodeName);
    formRef?.current.change('isSubjectDeleted', true);
  }, [formRef, ervkId, subjectFieldNames, dispatchKnmSetLegalEntityData, isNewFieldName, mspCodeName]);

  const renderAddButton = () => {
    if (hideAddRemove || isNewOrganization !== undefined || isSubjectWatchOnly || isLinkedByImpossibility) return null;

    return (
      <span className="shared-no-wrap-text">
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          onClick={addSubject}
        />
      </span>
    );
  };

  const IRIsSonko = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.SONKO_NKO_REGISTRY);
  const IRIsMsp = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.MSP_REGISTRY);

  const isTypeOP = pmType?.code === ProfKindCodesEnum.OP;
  const objectsPm = useField(`objectsPm`, { subscription: { value: true } }).input.value;
  const subjectTypePm = useField(`subjectTypePm`, { subscription: { value: true } }).input.value;
  const isRequired = useMemo(() => (isTypeOP ? !objectsPm?.length && !subjectTypePm : !subjectTypePm), [
    isTypeOP,
    objectsPm,
    subjectTypePm,
  ]);

  if (organizationsForSelect?.length) {
    return <ErknmSubjectSelectInPm organizationsForSelect={organizationsForSelect} />;
  }

  return (
    <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper}>
      <Section
        title={
          <>
            {sectionTitle}
            {isRequired && <span className={styles.RedMark}> *</span>}
          </>
        }
        type="list"
        id={pmSubjectBlockId}
        customButton={renderAddButton()}
      >
        <>
          {isLinkedByImpossibility && formRef?.current.getState()?.values.isSubjectDeleted && (
            <div className={styles.Yellow}>
              Для добавления сведений в раздел необходимо удалить и повторно добавить ссылку на паспорт
            </div>
          )}
          <FormSpy subscription={{ errors: true, touched: true }}>
            {props => {
              const fieldError = props.errors?.isOrganizationNew;
              const isFieldTouched = props.touched?.isOrganizationNew;

              if (fieldError && isFieldTouched) {
                return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
              }

              return null;
            }}
          </FormSpy>
          <Field name={isNewFieldName}>{() => null}</Field>
          {(isNewOrganization !== undefined || subjectType !== undefined) && (
            <>
              <PmSubjectItemComponent
                ervkId={ervkId}
                isNew={isNewOrganization}
                remove={removeSubject}
                subjectType={subjectType}
                inn={inn}
                ogrn={ogrn}
                snils={snils}
                isFiz={isFiz}
                rafpNza={rafpNza}
                kpp={kpp}
                okveds={okveds}
                organizationAutocomplete={organizationAutocomplete}
                watchOnly={isSubjectWatchOnly}
                checkSubjectResult={checkSubjectResult}
                smevRequestStatus={smevRequestStatus}
                isPresentInItRegistry={isPresentInItRegistry}
                itAccreditationResponse={itAccreditationResponse}
                smevItAccreditationRequestStatus={smevItAccreditationRequestStatus}
                name={name}
                subjectTypeLabel={subjectTypeLabel}
                getBlockId={getBlockId}
                inspectionResults={inspectionResults}
                isConnectedToErvkObject={isConnectedToErvkObject}
                checkByInnMessage={!ervkId ? checkByInnMessage : undefined}
                setCheckByInnMessage={!ervkId ? setCheckByInnMessage : undefined}
                isLinkedByImpossibility={isLinkedByImpossibility}
              />
              {(subjectType?.label === KnmSubjectTypesEnum.UR || subjectType?.label === KnmSubjectTypesEnum.IP) && (
                <Row>
                  <KnmMspField
                    id={`${fieldName}mspCode`}
                    name={mspCodeName}
                    value={getFormStateValues(formRef?.current)?.[mspCodeName]}
                    inspectionResult={IRIsMsp}
                    checkSubjectResult={checkSubjectResult}
                    ervkId={ervkId}
                    inn={inn}
                    formRef={formRef}
                    isShowMessages={false}
                  />
                </Row>
              )}

              <KnmSonkoField
                inn={inn}
                ogrn={ogrn}
                name={isSonkoName}
                value={isSonko}
                inspectionResult={IRIsSonko}
                subjectType={subjectType}
                id={`${fieldName}isSonko`}
                checkByInnMessage={!ervkId ? checkByInnMessage : undefined}
                formRef={formRef}
                isShowMessages={false}
              />
            </>
          )}
        </>
      </Section>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPmSubjectStoreProps => ({
  formRef: getKnmFormRef(state),
  isNewPm: getKnmIsNew(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
});

export const PmSubject = connect(mapStateToProps)(PmSubjectWithoutStore);
